import * as React from 'react';
import { VideoAlert } from '../video';
import { VIDEO_TYPE_TUTORIAL } from 'phoenix2/state/video/video';
import { useReducer } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

// The custom footer applied to the training modal adds around 60px to the original
// height of the modal. Some additional height reduction is needed to counter this
// in order to make sure the modal fits on shorter screens
const ADDITIONAL_HEIGHT_REDUCTION = 60;

interface ITrainingVideoProps {
    videoId: string;
    onReachedEnd: () => void;
    onEndAlert: () => void;
    onNextStep: () => void;
    isStepComplete: boolean;
    context?: string;
    instructionText?: string;
}

/**
 * A wrapper around the <VideoAlert /> component for training mode
 */
export const TrainingVideo = ({
    videoId,
    onReachedEnd,
    onEndAlert,
    context,
    isStepComplete,
    instructionText,
    onNextStep,
}: ITrainingVideoProps) => {
    const [videoWatched, setVideoWatched] = useReducer(() => true, false);

    // Show the messaging if the video gets watched while the modal is open or the step
    // was complete before opening the modal
    const showStepCompleteMessaging = videoWatched || isStepComplete;
    const onClick = () => {
        if (showStepCompleteMessaging) {
            onNextStep();
        }
        onEndAlert();
    };

    // Build the custom footer to be shown under the video. The step complete icon shows
    // when either the step is completed prior to opening the modal or the video is watched
    // while the modal is open
    const footer = (
        <div className="training-mode-video-step-container">
            <p className="training-mode-video-step-context">{context}</p>

            {showStepCompleteMessaging ? (
                <div className="training-mode-step-complete-title">
                    <div className="training-mode-step-complete-icon">
                        <FontAwesomeIcon
                            icon={faCheckCircle}
                            aria-hidden="true"
                            className="training-mode-check-animation"
                        />
                    </div>
                    Step complete
                </div>
            ) : (
                <p className="training-mode-video-step-instruction">{instructionText}</p>
            )}

            <button className="training-mode-video-step-button" onClick={onClick}>
                {showStepCompleteMessaging ? 'Next step' : 'Close'}
            </button>
        </div>
    );

    return (
        <VideoAlert
            videoId={videoId}
            onReachedVideoEnd={() => {
                setVideoWatched();
                onReachedEnd();
            }}
            videoType={VIDEO_TYPE_TUTORIAL}
            onEndAlert={onEndAlert}
            showSubtitles={true}
            forcePause={false}
            skipToFinalFrame={false}
            customFooter={footer}
            additionalHeightReduction={ADDITIONAL_HEIGHT_REDUCTION}
            outroLength={5}
        />
    );
};
